import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Json } from "../../core";
import type { ViewIntentOp } from "../../managers/assistant/types";

// AssistantBindingProvider — workspace-shell-level slot that holds the
// "what is the user currently looking at?" context. Populated by
// ActionManager (the surface that knows about the current action /
// view / record); read by AssistantPanel to decide contextual vs
// global mode, build the greeting, and dispatch view-intent ops.
//
// One provider at the shell; mutable via setBinding. ActionManager
// calls setBinding(value) on mount with the action's state + the
// applyOps callback that knows about its setters; calls setBinding(
// _DEFAULT_BINDING) on unmount so the panel reverts to global mode.

export interface AssistantBindingContextValue {
    /** Model key of the current action, e.g. "crm.lead". null = global. */
    anchoredModelKey: string | null;
    /** Action slug, e.g. "crm-leads". null = global. */
    anchoredActionKey: string | null;
    /** Current view type the user is on. */
    viewType: "list" | "form" | "kanban" | "pivot" | "calendar" | null;
    /** Live SearchPanel snapshot — domain / groupby / sort / search_text /
        columns. Per-turn payload to the assistant so it grounds on what
        the user is actually looking at right now. */
    viewState: Record<string, unknown>;
    /** Set when the current view is a single-record form, e.g. "QT-2026-…". */
    recordRef: string | null;
    /** display_name of the active record — used by greeting to greet by
        record name, not just screen name. */
    recordDisplayName: string | null;
    /** XML view id of the active view (render plan `view_id`). null when no
        plan is loaded. foundation.customization Phase 4C. */
    viewKey: string | null;
    /** Primary `ir.application.view` row uuid of the active view (render plan
        `view_parent_id`) — the parent_id a confirm card authors an extension
        against. null for file-only views → AI customization disabled. */
    viewParentId: string | null;
    /** Apply a list of view-intent ops emitted by a proposer tool. Each
        op kind maps to a state setter ActionManager owns. Implementations
        also call `runtime.services.assistant.notifyIntentApplied` to
        record provenance (best-effort, failures swallowed). */
    applyOps: (ops: ViewIntentOp[], onApplied?: (count: number) => void) => void;
}

export const _DEFAULT_BINDING: AssistantBindingContextValue = {
    anchoredModelKey: null,
    anchoredActionKey: null,
    viewType: null,
    viewState: {},
    recordRef: null,
    recordDisplayName: null,
    viewKey: null,
    viewParentId: null,
    applyOps: () => {},
};

interface BindingApi {
    value: AssistantBindingContextValue;
    setBinding: (next: AssistantBindingContextValue) => void;
    clearBinding: () => void;
}

const Ctx = createContext<BindingApi | null>(null);

export function AssistantBindingProvider({ children }: { children: ReactNode }) {
    const [value, setValue] = useState<AssistantBindingContextValue>(_DEFAULT_BINDING);
    const setBinding = useCallback((next: AssistantBindingContextValue) => {
        setValue(next);
    }, []);
    const clearBinding = useCallback(() => setValue(_DEFAULT_BINDING), []);

    const api = useMemo<BindingApi>(
        () => ({ value, setBinding, clearBinding }),
        [value, setBinding, clearBinding],
    );

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/** Read the current binding. Returns _DEFAULT_BINDING when no provider is
    mounted OR when no action has published a binding yet. */
export function useAssistantBinding(): AssistantBindingContextValue {
    const api = useContext(Ctx);
    return api?.value ?? _DEFAULT_BINDING;
}

/** Imperative setter — used by ActionManager to publish + clear its binding. */
export function useAssistantBindingSetter(): {
    setBinding: (b: AssistantBindingContextValue) => void;
    clearBinding: () => void;
} {
    const api = useContext(Ctx);
    if (!api) {
        // Returning no-ops keeps ActionManager harmless when rendered without
        // the provider (e.g. in story / fixture trees). Production wraps the
        // shell with the provider so this branch never fires.
        return { setBinding: () => {}, clearBinding: () => {} };
    }
    return { setBinding: api.setBinding, clearBinding: api.clearBinding };
}

/** Helper for ActionManager: build the canonical viewState slice the
    assistant grounds on. Keys mirror the legacy contract. */
export function buildViewState(args: {
    domain?: unknown;
    groupBy?: string[];
    sortField?: string | null;
    sortDir?: "asc" | "desc";
    searchText?: string;
    hiddenCols?: string[];
}): Record<string, unknown> {
    const vs: Record<string, unknown> = {};
    if (args.domain !== undefined) vs.domain = args.domain as Json;
    if (args.groupBy && args.groupBy.length > 0) vs.groupby_field = args.groupBy;
    if (args.sortField) {
        vs.sort = { field: args.sortField, direction: args.sortDir ?? "asc" };
    }
    if (args.searchText) vs.search_text = args.searchText;
    if (args.hiddenCols && args.hiddenCols.length > 0) vs.hidden_cols = args.hiddenCols;
    return vs;
}
